import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { 
  Lock, 
  Shield, 
  Heart, 
  ArrowRight, 
  Users,
  Ban,
  Mail,
  Package
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Trust strip items - compact, scannable
const trustItems = [
  { icon: Lock, text: "No data sold" },
  { icon: Mail, text: "No spam ever" },
  { icon: Ban, text: "No platforms notified" },
  { icon: Users, text: "Worker-led" },
];

export default function Home() {
  const [impactCount, setImpactCount] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Fade in animation
    setIsVisible(true);
    
    // Fetch impact stats
    fetch(`${API}/impact`)
      .then((res) => res.json())
      .then((data) => setImpactCount(data.total_sent))
      .catch(() => setImpactCount(847)); // Fallback
  }, []);

  return (
    <div className="min-h-[calc(100vh-80px)] overflow-hidden">
      {/* Hero Section - "One-breath" design */}
      <section className="relative py-16 md:py-24 lg:py-32 overflow-hidden" data-testid="home-hero">
        {/* Floating orbs background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1" />
          <div className="floating-orb floating-orb-2" />
          <div className="floating-orb floating-orb-3" />
        </div>
        
        <div className="container-pws relative z-10">
          <div className={`max-w-3xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {/* Worker-led badge */}
            <div className="worker-led-badge mb-6" data-testid="worker-led-badge">
              <Users className="h-3.5 w-3.5" />
              Built by workers, for workers
            </div>

            {/* Main headline - punchy, direct */}
            <h1
              className="font-['Space_Grotesk'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[color:var(--pws-charcoal)] tracking-tight leading-[1.1]"
              data-testid="home-hero-title"
            >
              Free panties.
              <br />
              <span className="text-[color:var(--pws-coral)]">No strings attached.</span>
            </h1>
            
            {/* Skepticism-acknowledging subhead */}
            <p
              className="mt-5 text-lg md:text-xl text-[color:var(--pws-ink)] max-w-xl leading-relaxed"
              data-testid="home-hero-subtitle"
            >
              Yeah, we know how that sounds. But there's no catch, no signup trap, 
              no "just one more thing." Just free underwear for people in the industry.
            </p>

            {/* Impact counter */}
            {impactCount && (
              <div className="mt-6 impact-counter" data-testid="impact-counter">
                <span className="impact-number text-3xl md:text-4xl font-bold">
                  {impactCount.toLocaleString()}
                </span>
                <span className="ml-2 text-[color:var(--pws-stone)] text-lg">
                  pairs sent. No strings.
                </span>
              </div>
            )}

            {/* Primary CTA - warm, inviting */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="btn-warm btn-warm-pulse text-lg px-8 h-14 rounded-xl"
                data-testid="hero-get-free-pair-btn"
              >
                <Link to="/request">
                  Get mine
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-[color:var(--pws-line)] text-[color:var(--pws-ink)] hover:bg-[color:var(--pws-cream)] h-14 rounded-xl"
                data-testid="hero-learn-why-btn"
              >
                <Link to="/pledge">What's the catch?</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip - compact, scannable */}
      <div className="trust-strip" data-testid="trust-strip">
        {trustItems.map((item, index) => (
          <div key={index} className="trust-strip-item" data-testid={`trust-strip-item-${index}`}>
            <item.icon />
            <span>{item.text}</span>
          </div>
        ))}
      </div>

      {/* What This Is / Isn't - Direct headers */}
      <section className="py-16 md:py-20 bg-[color:var(--pws-off)]" data-testid="what-this-is-section">
        <div className="container-pws">
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <div>
              <h2 className="font-['Space_Grotesk'] text-2xl md:text-3xl font-semibold text-[color:var(--pws-charcoal)]">
                Here's the deal
              </h2>
              <p className="mt-4 text-lg text-[color:var(--pws-ink)] leading-relaxed">
                Free underwear for everyone in the industry. You fill out a form, 
                we send you a pair. That's it. You don't owe us anything—not your 
                story, not your time, not your email for a newsletter.
              </p>
            </div>
            <div>
              <h2 className="font-['Space_Grotesk'] text-2xl md:text-3xl font-semibold text-[color:var(--pws-charcoal)]">
                What's the catch?
              </h2>
              <p className="mt-4 text-lg text-[color:var(--pws-ink)] leading-relaxed">
                There isn't one. Not a rescue program. Not a mailing list trap. 
                Not data collection for platforms or cops. Just underwear, 
                from people who've been in the industry and get it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Objection Handler + Data Pledge Preview */}
      <section className="py-16 md:py-20 bg-[color:var(--pws-cream)]" data-testid="pledge-preview-section">
        <div className="container-pws">
          <div className="max-w-2xl mx-auto">
            {/* Objection handler box */}
            <div className="objection-box mb-8" data-testid="objection-box">
              <p className="text-[color:var(--pws-ink)] text-lg leading-relaxed">
                <span className="font-semibold">"But why?"</span> Because basics matter. 
                Because underwear is personal, practical, and often the last thing 
                you prioritize when money's tight. And because sometimes the best 
                way to help is to just... help. No agenda.
              </p>
            </div>

            <Card className="bg-white border-[color:var(--pws-line)] shadow-sm">
              <CardContent className="p-8 md:p-10">
                <h2 className="font-['Space_Grotesk'] text-2xl md:text-3xl font-semibold text-[color:var(--pws-charcoal)]">
                  Our promise
                </h2>
                <p className="mt-4 text-[color:var(--pws-ink)] leading-relaxed">
                  We know how it goes—someone "helps" and suddenly your info is everywhere. 
                  Not here. We keep nothing we don't need, share nothing, sell nothing. 
                  You can ghost us anytime.
                </p>
                <ul className="mt-6 space-y-3 text-[color:var(--pws-ink)]">
                  <li className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-[color:var(--pws-plum)] mt-0.5 flex-shrink-0" />
                    <span>Only collect what's needed to ship your pair</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Lock className="h-5 w-5 text-[color:var(--pws-plum)] mt-0.5 flex-shrink-0" />
                    <span>Never share with platforms or law enforcement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Heart className="h-5 w-5 text-[color:var(--pws-plum)] mt-0.5 flex-shrink-0" />
                    <span>No government name or ID required</span>
                  </li>
                </ul>
                <div className="mt-8">
                  <Button
                    asChild
                    variant="outline"
                    className="border-[color:var(--pws-plum)] text-[color:var(--pws-plum)] hover:bg-[color:var(--pws-plum)] hover:text-white"
                    data-testid="read-full-pledge-btn"
                  >
                    <Link to="/pledge">Read the full pledge</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-20 bg-[color:var(--pws-off)]" data-testid="footer-trust-section">
        <div className="container-pws text-center">
          <div className="flex justify-center mb-4">
            <Package className="h-12 w-12 text-[color:var(--pws-coral)] package-animation" />
          </div>
          <h2 className="font-['Space_Grotesk'] text-2xl md:text-3xl font-semibold text-[color:var(--pws-charcoal)]">
            Ready when you are
          </h2>
          <p className="mt-3 text-[color:var(--pws-stone)] text-lg max-w-md mx-auto">
            Takes about 30 seconds. Skip anything you want.
          </p>
          <div className="mt-8">
            <Button
              asChild
              size="lg"
              className="btn-warm text-lg px-8 h-14 rounded-xl"
              data-testid="footer-get-free-pair-btn"
            >
              <Link to="/request">
                Get mine
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
